import $ from 'jquery';
import _ from 'underscore';
import pageContext from '@bingads-webui-static-legacy/page-context';
import expDateUtils from '@bingads-webui-campaign-legacy/experiments-tab/experiment-date-utils-loader';

import { DeliveryStatusPopupFactory } from '@bingads-webui-campaign-legacy/common-grid-config/column/delivery-status/index';
import { openInlineOpportunitiesPanel } from '@bingads-webui-campaign-legacy/campaigns-tab-campaigns/grid/inlineopportunity/index';

import {
  dismissBudgetLandscapeHelpInfo,
  showBudgetLandscapeHelpInfo,
  showBidLandscapeHelpInfo,
  openBudgetLandscapePanel,
  getBudgetLandscapeHelpInfoTemplate,
} from '@bingads-webui-campaign-legacy/campaigns-tab-campaigns/grid/budgetlandscape/index';
import deliverStatusDetailTemplate from '@bingads-webui-campaign-legacy/common-grid-config/column/delivery-status/popup/delivery-status-details-popup.pug';
import { BLACKLIST } from '@bingads-webui-campaign/delivery-status-popup-factory';
import {openBudgetSuggestionDialog} from '../budget-suggestion/index';

import autobiddingTemplate from './autobidding-popover-template.pug';

const i18nGlobal = window.globalLegacyI18n.get('component/i18n/loader!legacy/global');

const isExperimentsOpenBetaEnabled = _.result(pageContext.Permissions, 'IsExperimentsOpenBetaEnabled');
const isReadOnlyUser = _.result(pageContext.Permissions, 'IsReadOnlyUser');
const showBudgetSuggestion = _.result(pageContext.Permissions.dynamic, 'EnableBudgetSuggestion');

let showCampaignRejectionReasons = (event, campaign) => {
  return () => {
    const blackList = [
      'InlineOpportunity',
      'BudgetSuggestions',
      'CampaignBudgetPaused',
    ];
    const details = _.difference(campaign.DeliveryStatusDetails, blackList);

    /**
      * Below code is added for in-context repair recommmendation feature in old UI.
      * For the following 3 delivery status, it needs to add a link in the popup content for
      * new UI, but the pure text for old UI. So, we provide different i18n string saperately
      * for new UI and old UI.
      * 'deliveryStatus' is to get the i18n string with link.
      * 'deliveryStatus_WithoutLink' is to get the pure text i18n string without link.
    */
    const toBeUpdated = ['NoEligibleAdGroups', 'NoEligibleAds', 'NoEligibleKeywords'];

    toBeUpdated.forEach((NoEligible) => {
      const findIndex = details.indexOf(NoEligible);

      if (findIndex >= 0) {
        details.splice(findIndex, 1, `${NoEligible}_WithoutLink`);
      }
    });

    const params = {
      i18n: i18nGlobal,
      DeliveryStatusDetails: details,
      collectionSize: details.length > 3 ?
        3 : details.length, // max of 3 reasons shown, as in other grids
      $,
    };

    if (isExperimentsOpenBetaEnabled && campaign.ExperimentId && campaign.AssociatedExperiment) {
      params.ExperimentId = campaign.ExperimentId;
      params.AssociatedExperiment = campaign.AssociatedExperiment;
      params.statusForCombinedPopup = campaign.statusForCombinedPopup;
      params.percentSymbol = pageContext.DecimalFormatter.percentSymbol;
      params.expDateUtils = expDateUtils;
    }

    const detailsHtml = deliverStatusDetailTemplate(params);

    $.gridPopup.Callback(detailsHtml); // eslint-disable-line new-cap
  };
};

/**
 * Manages creation of delivery status column popups for the Campaigns grid
 */
class CampaignDeliveryStatusPopupFactory extends DeliveryStatusPopupFactory {
  constructor({
    grid,
    perfMarker,
    scope,
    instrumentation,
  }) {
    super({
      grid,
      handler: showCampaignRejectionReasons,
    });

    this.perfMarker = perfMarker;
    this.scope = scope;
    this.instrumentation = instrumentation;
  }
  /**
    * Creates campaign entity object from event target using the grid associated with the factory.
    *
    * @param {Object} event - The backbone click event from where the quality score cell was clicked.
    * @returns {Object} campaign entity - An object with properties containing Campaign Id and Name.
  */
  getEventTargetEntity(event) {
    const model = this.getEventTargetModel(event);

    const result = {
      Id: model.Id,
      Name: model.Name,
      Budget: model.Budget,
      DeliveryStatus: model.DeliveryStatus,
      DeliveryStatusDetails: _.clone(model.DeliveryStatusDetails),
    };

    if (isExperimentsOpenBetaEnabled && model.ExperimentId && model.AssociatedExperiment) {
      result.ExperimentId = model.ExperimentId;
      result.AssociatedExperiment = model.AssociatedExperiment;
      result.statusForCombinedPopup = [
        'CampaignUserPaused',
        'InvalidFinancialStatusOther',
        'CampaignSuspended',
        'AutoBiddingLimitedByNotEnoughConversions',
      ];

      if ($(event.target).hasClass('experiment-delivery-status-override') ||
        _.contains(['Promoted', 'Graduated', 'Ended'], model.AssociatedExperiment.Status)
      ) {
        // Show experiment info only
        result.DeliveryStatusDetails = ['ExperimentInfo'];
      } else if (!_.contains(result.statusForCombinedPopup, result.DeliveryStatus)) {
        // Show original info and experiment info in different pages
        result.DeliveryStatusDetails = _.union(result.DeliveryStatusDetails, ['ExperimentInfo']);
      }
      // Otherwise show original info combined with experiment info within the same page
    }

    return result;
  }

  /**
    * show budget landscape
    *
    * @param {Object} event - The backbone click event from where the quality score cell was clicked.
    * @param {Boolean} isForVNext - Is new ui or not.
    * @param {Object} serverContext -  Server Context
    * @param {Object} activity  - Activity logger.
    * @param {Object} newI18n - new I18n Localization component
    * @returns {undefined}.
  */
  showBudgetLandscapePopup(event,  isForVNext = false, serverContext = null, activity = null, newI18n = {}) {
    dismissBudgetLandscapeHelpInfo();
    const entity = this.getEventTargetModel(event);

    if (showBudgetSuggestion) {
      return openBudgetSuggestionDialog(event, entity, this.grid, serverContext, activity, newI18n, this.instrumentation);
    } else {
      return openBudgetLandscapePanel(event, entity, this.grid, isForVNext);
    }
  }

  /**
    * show inilne opportunity
    *
    * @param {Object} event - The backbone click event from where the quality score cell was clicked.
    * @param {Boolean} isForVNext - Is new ui or not.
    * @returns {undefined}.
  */
  showInlineOpportunityPanel(event, isForVNext) {
    let deliveryStatusPopupPerfMarker;

    if (this.perfMarker) {
      deliveryStatusPopupPerfMarker = this.perfMarker.createChild('Delivery_Status_Opportunity_Popup');
    }

    const entity = this.getEventTargetEntity(event);

    return openInlineOpportunitiesPanel(event, entity, this.grid, isForVNext, deliveryStatusPopupPerfMarker);
  }

  showBudgetLandscapeHelpInfo(event, isForVNext = false) {
    const entity = this.getEventTargetModel(event);

    return showBudgetLandscapeHelpInfo(event, entity, this.grid, isForVNext);
  }

  getBudgetLandscapeHelpInfoTemplate(event) {
    const entity = this.getEventTargetModel(event);

    return getBudgetLandscapeHelpInfoTemplate(entity);
  }

  getAutobiddingLimitedHelpInfoTemplate(deliveryStatus, index, event) {
    const entity = this.getEventTargetModel(event);
    const campaignDeliveryStatusDetails = _.filter(entity.CampaignDeliveryStatusDetails, (detail) => !_.contains(BLACKLIST, detail.Status));
    const detail = campaignDeliveryStatusDetails && campaignDeliveryStatusDetails[index];
    const isPortfolio = detail && detail.Entity === 'PortfolioBidStrategy';
    let suggestedValue = detail && detail.RecommendedValue;
    const targetType = entity.BiddingScheme.Type;
    const showApply = !isPortfolio && deliveryStatus !== 'AutoBiddingLimitedInventoryLimit' && deliveryStatus !== 'AutoBiddingPortfolioBidStrategyTrainingStatus' && deliveryStatus !== 'AutoBiddingLimitedUnAttainableTarget';
    const applyStringMap = {
      AutoBiddingLimitedMaxCPCRestriction: 'ApplyRecommendedCPC',
      AutoBiddingLimitedBudgetRestriction: 'ApplyRecommendedBudget',
      AutoBiddingLimitedUnAttainableTarget: 'ApplyRecommendedTarget',
    };

    suggestedValue = suggestedValue || 0;

    let formattedSuggestedValue = i18nGlobal.realI18n.formatDecimal(suggestedValue, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    let detailMessage = i18nGlobal.getString(`Message_DeliveryStatus_${deliveryStatus}${isPortfolio ? '_Portfolio' : ''}`).replace('{suggestedValue}', formattedSuggestedValue);

    if (deliveryStatus === 'AutoBiddingLimitedUnAttainableTarget') {
      switch (targetType) {
      case 'MaxConversions':
      case 'TargetCpa':
        detailMessage = i18nGlobal.getString('Message_DeliveryStatus_AutoBiddingLimitedUnAttainableTarget_TCPA');
        break;
      case 'MaxConversionValue':
      case 'TargetRoas':
        detailMessage = i18nGlobal.getString('Message_DeliveryStatus_AutoBiddingLimitedUnAttainableTarget_TROAS');
        break;
      case 'TargetImpressionShare': // no support from backend
      default:
        break;
      }
    }

    const activity = this.instrumentation && this.instrumentation.autobiddingLimitedStatus.create().hoverDeliveryStatus.create();

    if (activity) {
      activity.trace(JSON.stringify({
        campaignId: entity.Id,
        deliveryStatus,
        suggestedValue,
        isPortfolio,
      }));
    }

    return autobiddingTemplate({
      deliveryStatus,
      detailMessage,
      learnMoreString: i18nGlobal.getString('LearnMoreAboutThisStatus'),
      showApply,
      applyString: showApply ? i18nGlobal.getString(applyStringMap[deliveryStatus]) : '',
      enableApply: !isReadOnlyUser,
    });
  }

  showLandscapeDisabledHelpInfo(event) {
    const entity = this.getEventTargetModel(event);
    const { DeliveryStatus } = entity;

    return showBidLandscapeHelpInfo(event, { landscapeDisabled: true, DeliveryStatus });
  }

  dismissLandscapeDisabledHelpInfo() {
    dismissBudgetLandscapeHelpInfo();
  }
}

export { CampaignDeliveryStatusPopupFactory };
